import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { galleriesApi } from '@/lib/api';
import { Camera, Image } from 'lucide-react';

const GalleriesPage = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGalleries();
  }, []);

  const loadGalleries = async () => {
    try {
      const data = await galleriesApi.getAll();
      setGalleries(data);
    } catch (error) {
      console.error('Error loading galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-r from-stone-800 to-stone-700 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Camera className="h-16 w-16 text-white mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Photo Galleries
          </h1>
          <p className="text-xl text-stone-300 max-w-2xl mx-auto">
            Browse photos from church events and celebrations
          </p>
        </div>
      </section>

      {/* Galleries Grid */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700 mx-auto"></div>
              <p className="mt-4 text-stone-600">Loading galleries...</p>
            </div>
          ) : galleries.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleries.map((gallery) => (
                <Link key={gallery.id} to={`/galleries/${gallery.id}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group" data-testid="gallery-card">
                    <div className="aspect-video bg-stone-200 relative overflow-hidden">
                      {gallery.cover_photo_url ? (
                        <img
                          src={gallery.cover_photo_url}
                          alt={gallery.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-amber-200">
                          <Image className="h-16 w-16 text-amber-400" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors"></div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="text-lg font-semibold text-stone-800 mb-1 group-hover:text-amber-700 transition-colors">
                        {gallery.title}
                      </h3>
                      {gallery.description && (
                        <p className="text-stone-600 text-sm line-clamp-2 mb-2">
                          {gallery.description}
                        </p>
                      )}
                      <p className="text-stone-500 text-xs">
                        Created {formatDate(gallery.created_at)}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8 text-center">
                <Camera className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-stone-800 mb-2">No Galleries Yet</h3>
                <p className="text-stone-600">
                  Check back soon for photo galleries from church events.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default GalleriesPage;
